import apiClient from '../config/axios';
import { ENV_CONFIG } from '../config/environment';

export interface LowStockItem {
  id: number;
  attributes: {
    branch_medicine_id: number;
    branch: {
      id: number;
      branch_name: string;
    };
    medicine: {
      id: number;
      medicine_name: string;
      manufacturer?: string;
      category_name?: string;
    };
    current_stock: number;
    status: string;
    last_updated: string;
    branch_relationship?: {
      relationship_id: string;
      relationship_status: string;
      total_order_count: number;
      total_order_value: number;
    };
  };
}

export interface LowStockResponse {
  status: string;
  data: LowStockItem[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      pageCount: number;
    };
  };
  seller_id?: number;
  branch_id?: number;
  threshold: number;
  total_branches?: number;
  filtered_branches?: number[];
  message?: string;
}

class LowStockService {
  /**
   * Get low stock medicines for seller's associated branches
   */
  async getSellerBranchesLowStock(
    sellerId: number,
    threshold?: number,
    params?: {
      page?: number;
      pageSize?: number;
      branch_ids?: number[];
    }
  ): Promise<LowStockResponse> {
    const queryParams: any = {
      threshold: threshold ?? ENV_CONFIG.LOW_STOCK_THRESHOLD,
      page: params?.page || 1,
      pageSize: params?.pageSize || ENV_CONFIG.PAGINATION_DEFAULT_PAGE_SIZE,
    };
    
    // Convert branch_ids array to comma-separated string if provided
    if (params?.branch_ids && params.branch_ids.length > 0) {
      queryParams.branch_ids = params.branch_ids.join(',');
    }

    const response = await apiClient.get(`/branch-medicines/seller/${sellerId}/low-stock`, {
      params: queryParams,
    });
    return response.data;
  }

  /**
   * Get low stock medicines for a specific branch
   */
  async getBranchLowStock(
    branchId: number,
    threshold?: number,
    params?: {
      page?: number;
      pageSize?: number;
    }
  ): Promise<LowStockResponse> {
    const response = await apiClient.get(`/branch-medicines/branch/${branchId}/low-stock`, {
      params: {
        threshold: threshold ?? ENV_CONFIG.LOW_STOCK_THRESHOLD,
        page: params?.page || 1,
        pageSize: params?.pageSize || ENV_CONFIG.PAGINATION_DEFAULT_PAGE_SIZE,
      },
    });
    return response.data;
  }

  /**
   * Get current seller ID from localStorage
   */
  getCurrentSellerId(): number | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (error) {
      console.error('Error getting current seller ID:', error);
    }
    return null;
  }

  /**
   * Get current branch ID from localStorage
   */
  getCurrentBranchId(): number | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.branch_id || null;
      }
    } catch (error) {
      console.error('Error getting current branch ID:', error);
    }
    return null;
  }

  /**
   * Check if current user is a seller (has is_main_seller = true)
   */
  isSeller(): boolean {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.is_main_seller === true;
      }
    } catch (error) {
      console.error('Error checking if user is seller:', error);
    }
    return false;
  }

  /**
   * Get seller's associated branches
   */
  async getSellerBranches(sellerId: number): Promise<any[]> {
    try {
      const response = await apiClient.get('/branch-seller-relationships', {
        params: {
          'filters[seller_id][$eq]': sellerId,
          'filters[relationship_status][$eq]': 'active',
          'pagination[pageSize]': 1000,
          'populate[branch][fields][0]': 'branch_name',
          'populate[branch][fields][1]': 'id',
        },
      });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching seller branches:', error);
      return [];
    }
  }
}

const lowStockService = new LowStockService();
export default lowStockService;
